import api from './core/api';
import { Admin } from './core/types';

export interface LoginResponse {
  admin: Admin;
  token: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

class AuthService {
  /**
   * Admin login with username and password
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
      '/admin/login',
      {
        username,
        password,
      }
    );
    return response.data.data;
  }

  /**
   * Get current admin profile
   */
  async getProfile(): Promise<Admin> {
    const response = await api.get<ApiResponse<Admin>>('/admin/profile');
    return response.data.data;
  }

  /**
   * Send OTP code (alternative login method)
   */
  async sendCode(email: string): Promise<void> {
    await api.post('/admin/send-code', { email });
  }

  /**
   * Verify OTP code
   */
  async verifyCode(email: string, code: string): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
      '/admin/verify-code',
      {
        email,
        code,
      }
    );
    return response.data.data;
  }

  /**
   * Logout (clear local storage)
   */
  logout(): void {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  }

  /**
   * Check if admin is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('adminToken');
    return !!token;
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem('adminToken');
  }

  /**
   * Store token and admin info
   */
  setAuth(token: string, admin: Admin): void {
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminUser', JSON.stringify(admin));
  }

  /**
   * Get stored admin info
   */
  getStoredAdmin(): Admin | null {
    const adminStr = localStorage.getItem('adminUser');
    if (!adminStr) return null;
    try {
      return JSON.parse(adminStr);
    } catch {
      return null;
    }
  }
}

export const authService = new AuthService();
export default authService;
